import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';
import { useHistory } from 'react-router';

import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getPublicPortalURL } from 'CorePackage/MainModule/reducers/coreReducer';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmHistories } from 'KnmPackage/KnmHistoriesModule/components/KnmHistories';
import { KnmSignInfo } from 'KnmPackage/KnmSignInfoModule/components/KnmSignInfo';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmAdminCanEditSubject,
  getKnmDataVersion,
  getKnmEventTypesSelectOptions,
  getKnmFormRef,
  getKnmIsErknmStatusTypeCanceled,
  getKnmIsErknmStatusTypeCantBeCarryOut,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsNeedActSaveOrSign,
  getKnmIsNew,
  getKnmIsWatchOnly,
  getKnmStatusCode,
  getKnmWatchOnlyByTestUser,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { PmCollaborating } from 'KnmPmPackage/PmCollaboratingModule/components/PmCollaborating';
import { PmInfo } from 'KnmPmPackage/PmInfoModule/components/PmInfo';
import { PmInspectors } from 'KnmPmPackage/PmInspectorsModule/components/PmInspectors';
import { PmObjects } from 'KnmPmPackage/PmObjectsModule/components/PmObjects';
import { PmReasons } from 'KnmPmPackage/PmReasonsModule/components/PmReasons';
import { PmResultOfInspection } from 'KnmPmPackage/PmResultOfInspection/components/PmResultOfInspection';
import { PmSubject } from 'KnmPmPackage/PmSubjectModule/components/PmSubject';
import { PmWarning } from 'KnmPmPackage/PmWarningModule/components/PmWarning';
import { PmEvents } from 'KnmPmPackage/PmEventsModule/components/PmEvents';
import { PmExtension } from 'KnmPmPackage/PmExtensionModule/components/PmExtension';
import { ErknmExperts } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmExperts';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IEventType } from 'APIPackage/DictionariesModule/interfaces/IEventType';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { PmOpCancel } from 'KnmPmPackage/PmOpCancelModule/components/PmOpCancel';
import { KnmDocuments } from 'KnmPackage/KnmDocumentsModule/components/KnmDocuments';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { ErknmAgreements } from 'KnmErknmPackage/ErkmAgreementsModule/ErknmAgreements';
import { ErknmRepeatPassports } from 'KnmErknmPackage/ErknmRepeatPassportsModule/ErknmRepeatPassports';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { newFormOrganization } from '../../../KnmErknmPackage/MainModule/constants/newFormOrganization';
import { IKnmErknmFormSubject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { KnmEpguNotification } from '../../../KnmPackage/KnmEpguNotificationModule/components/KnmEpguNotification';
import { KnmNotifications } from '../../../KnmPackage/KnmNotificationModule/components/KnmNotifications';
import { PmEventSubject } from '../../PmEventSubjectModule/components/PmEventSubject';
import { PmReject } from '../../PmRejectModule/components/PmReject';
import { knmPmResetInitialFormValues } from '../actions/knmPmActions';
import { useKnmPm } from '../hooks/useKnmPm';
import { IKnmPmForm } from '../interfaces/IKnmPmForm';
import { getKnmPmInitialFormValues } from '../selectors/knmPmSelectors';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAccountProsecutorOrganizationsWithDistrictSelectOptions } from '../../../CorePackage/AccountModule/selectors/accountSelectors';
import { IUserOrganisationSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { PROSECUTOR_ORGANIZATION_BLOCK_ID } from '../../../KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { CreatePmModal } from './CreatePmModal';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';

interface IKnmPmContentProps {
  knmData: IKnm | undefined;
  values: IKnmPmForm;
}

interface IKnmPmContentStoreProps {
  publicPortalURL: string;
  statusCode: IKnmStatus['code'] | undefined;
  formRef: IKnmFormRef | undefined;
  isWatchOnly: boolean;
  initialFormValues?: IKnmPmForm;
  isNew: boolean;
  settings: IAdminSetting[];
  isSupervisor: boolean;
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  isWatchOnlyByTestUser: boolean;
  isErknmStatusTypeCanceled: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCantBeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  eventTypesSelectOptions: IDictionarySelectOption<IEventType>[] | undefined;
  isNeedActSaveOrSign?: boolean;
  isErp: boolean;
  isKnmAdminCanEditSubject: boolean;
}

const KnmPmContentWithoutStore = ({
  knmData,
  values,
  formRef,
  isWatchOnly,
  publicPortalURL,
  statusCode,
  initialFormValues,
  isNew,
  settings,
  userProsecutorOrganisationsSelectOptions,
  isWatchOnlyByTestUser,
  eventTypesSelectOptions,
  isErknmStatusTypeCanceled,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCantBeCarriedOut,
  isKnmStatusTypeCompleted,
  isSupervisor,
  isNeedActSaveOrSign = false,
  isErp,
  isKnmAdminCanEditSubject,
}: IKnmPmContentProps & IKnmPmContentStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();
  const [isShownCreatePmModal, setIsShownCreatePmModal] = useState(true);
  const knoOrganizationPmDictVerId = values?.knoOrganizationPm?.value.dictVersionId;
  const supervisionTypePmDictVerId = values?.supervisionTypePm?.value.dictVersionId;

  const isErvkProsecutorOrganizationStartFrom = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.ERVK_PROSECUTOR_ORGANIZATION_4_1_15_STARTS_FROM,
  );

  const isEnablePaperlessPassportDocument = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );
  const isEnableAgreements = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_16_STARTS_FROM,
  );
  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );
  const isEnablePmLimitationOfSuspension = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.PM_LIMITATION_OF_SUSPENSION_STARTS_FROM,
  );

  const isDisableFieldByCompleteStatuses =
    isSupervisor && (isKnmStatusTypeCarriedOut || isKnmStatusTypeCompleted || isKnmStatusTypeCantBeCarriedOut);

  const isObjectsWatchOnly = isDisableFieldByCompleteStatuses || isNeedActSaveOrSign;

  const isShowProsecutorOrganization =
    knmData?.createdBy?.username === 'SMEV' &&
    !knmData.prosecutorOrganization?.ervkUuid &&
    isErvkProsecutorOrganizationStartFrom;

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.PM_CANCELED_REASONS));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_TYPES_CANCELED));
    dispatch(catalogGet(CommonCatalogsNamesEnum.UAV_TYPES));
  }, [dispatch]);

  useEffect(() => {
    if (!isNew) {
      formRef?.current.reset(initialFormValues);
    }
  }, [formRef, initialFormValues]);

  useUnmount(() => {
    dispatch(knmPmResetInitialFormValues());
  });

  useKnmPm(values);
  const pmType = values?.pmType;
  const pmTypeCode = values.pmType?.code;
  const isFiz = values?.isFiz;
  const isTypePmCodeOP = pmTypeCode === ProfKindCodesEnum.OP;

  useEffect(() => {
    if (!values.wasSelectedOrganizationByOtherPassport) {
      formRef?.current.change('ogrn');
      formRef?.current.change('organizationName');
      formRef?.current.change('okveds');
    }
  }, [formRef, isFiz]);

  useEffect(() => {
    values?.approveDocs?.forEach((item, index) => {
      formRef?.current.change(`approveDocs[${index}].approveDoc` as keyof IKnmForm);
    });
  }, [knoOrganizationPmDictVerId, supervisionTypePmDictVerId]);

  const organization: IKnmErknmFormSubject = useMemo(
    () => ({
      ...newFormOrganization,
      inn: values.inn,
      ogrn: values.ogrn,
      okveds: values.okveds,
      organizationName: values.organizationName,
      guid: values.organizationGuid,
      isNew,
    }),
    [values, isNew],
  );

  const isPmFormWatchOnly: boolean = isWatchOnly || (!isNew && statusCode !== KnmErknmStatusCodesEnum.FILL_IN_PROCESS);

  const riskIndicators = useMemo(() => {
    let indicators: IKnmFormRiskIndicators[] = [];

    values.reasonsPm?.forEach(e => {
      if (Array.isArray(e?.riskIndicators)) {
        indicators = [...indicators, ...e.riskIndicators];
      }
    });

    return indicators;
  }, [values.reasonsPm]);

  const pmSubject: IKnmErknmFormSubject | undefined = useMemo(() => {
    if (!values.subjectTypePm) {
      return;
    }

    return {
      subjectType: values.subjectTypePm,
      inn: values.inn,
      snils: values.snils,
      organizationName: values.organizationName,
      ogrn: values.ogrn,
      locationAddress: values.locationAddress,
      workAddress: values.workAddress,
      okveds: values.okveds,
      factualAddress: values.factualAddress,
      document: values.document,
      country: values.country,
      rafpNza: values.rafpNza,
      kpp: values.kpp,
      isFiz: values.isFiz,
      nkoForeignAgentsRegistryNumber: values.nkoForeignAgentsRegistryNumber,
      nkoForeignAgentsRegistryInclusionDate: values.nkoForeignAgentsRegistryInclusionDate,
      nkoForeignAgentsRegistryExclusionDate: values.nkoForeignAgentsRegistryExclusionDate,
      isNko: !!values.nkoForeignAgentsRegistryInclusionDate,
      ervkId: values.ervkId,
      guid: values.organizationGuid,
      isNew: values.isOrganizationNew ?? true,
      isAutocomplete: values.organizationAutocomplete,
      isSonko: values.isSonko,
      smevRequestStatus: values.smevRequestStatus,
      mspCode: values.mspCode,
      mspRegistryDate: values.mspRegistryDate,
      checkSubjectResult: values.checkSubjectResult ?? null,
      listId: uuidv4(),
      addressFns: undefined,
      dateCreate: undefined,
      epguNotifications: undefined,
      firstOrganizationName: undefined,
      isForeignSubjectAgencyNotRegistredInRf: undefined,
      isEmpty: undefined,
      isDocumentOrganization: false,
      position: values.position,
      isPresentInItRegistry: values.isPresentInItRegistry,
      itAccreditationResponse: values.itAccreditationResponse,
      smevItAccreditationRequestStatus: values.smevItAccreditationRequestStatus,
      inspectionResults: values.inspectionResults,
      isFilledFromLink: values.isFilledFromLink,
      locationregion: undefined,
      locationnotInFIAS: undefined,
      locationmanualAddress: undefined,
      locationfiasRegion: undefined,
    };
  }, [values]);

  const backUrl: string | null = useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    return params.get('backUrl');
  }, []);

  const deleteInitialInvalidFieldsPm = () => {
    sessionStorage.removeItem('initialValuesPm');
    sessionStorage.removeItem('initialInvalidFields');
  };

  const closeCreatePmModal = () => {
    setIsShownCreatePmModal(false);
    deleteInitialInvalidFieldsPm();

    if (backUrl) {
      history.push(backUrl);
    } else {
      window.location.href = '/private/preventions';
    }
  };

  const onCloseSuccess = () => {
    setIsShownCreatePmModal(false);
  };

  const isLinkedByImpossibility = values.reasonsPm?.some(
    reason =>
      reason.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS && reason.links && reason.links.length > 0,
  );

  return (
    <>
      {isNew && (
        <CreatePmModal
          visible={isShownCreatePmModal}
          values={values}
          onClose={closeCreatePmModal}
          onCloseSuccess={onCloseSuccess}
          knoOrganizationPm={values.knoOrganizationPm}
          reasonsPmValue={values.reasonsPm}
          startDate={values.startDate}
          stopDate={values.stopDate}
        />
      )}
      <PmInfo
        erpId={knmData?.erpId}
        eventsPm={values.eventsPm}
        accessToken={knmData?.pmErknm?.accessToken}
        className={knmSharedStyles.Section}
        isWatchOnly={isWatchOnly}
        statusCode={statusCode}
        pmType={pmType}
        publicPortalURL={publicPortalURL}
        created={values.created}
        rescheduling={values.rescheduling}
        abrogation={values.abrogation}
        signed={knmData?.signed}
        signatureInfo={knmData?.signatureInfo}
        originalStopDate={values.originalStopDate}
        suspensionEvents={values.suspensionEvents}
        extension={values.periodExtension}
        interactionCategory={values.interactionCategory}
      />

      {!isEnablePmLimitationOfSuspension && (
        <PmExtension
          extension={values.periodExtension}
          watchOnly={isWatchOnly}
          events={values.eventsPm}
          isUnableToRefuse={values.isUnableToRefuse}
          pmTypeCode={pmTypeCode}
        />
      )}

      {pmTypeCode === ProfKindCodesEnum.OP && (
        <PmWarning
          className={knmSharedStyles.Section}
          isWatchOnly={isWatchOnly}
          contentWarningAttachmentsNumber={values.contentWarningAttachments?.length || 0}
          objectionWarningAttachmentsNumber={values.objectionWarningAttachments?.length || 0}
          exampleWarningAttachmentsNumber={values.exampleWarningAttachments?.length || 0}
          created={values.created}
          formRef={formRef}
        />
      )}

      {isEnablePaperlessPassportDocument && isErknmStatusTypeCanceled && pmTypeCode === ProfKindCodesEnum.OP && (
        <PmOpCancel cancellationAttachment={values.cancellationAttachment} />
      )}
      <PmReasons
        className={knmSharedStyles.Section}
        watchOnly={isPmFormWatchOnly}
        pmTypeCode={pmTypeCode}
        reasonsPm={values.reasonsPm}
      />

      {(isShowProsecutorOrganization ||
        values.prosecutorOrganizationErknm ||
        (isNew && userProsecutorOrganisationsSelectOptions)) && (
        <div className={knmSharedStyles.Section}>
          <FieldBlock label="Наименование прокуратуры" id={PROSECUTOR_ORGANIZATION_BLOCK_ID}>
            <SelectInputField
              id="prosecutorOrganizationErknm"
              name="prosecutorOrganizationErknm"
              options={userProsecutorOrganisationsSelectOptions}
              isClearable
              isSearchable={!isShowProsecutorOrganization}
              disabled={!isShowProsecutorOrganization}
              size="medium"
            />
          </FieldBlock>
        </div>
      )}
      <PmObjects
        className={knmSharedStyles.Section}
        watchOnly={isKnmAdminCanEditSubject ? false : isObjectsWatchOnly}
        isLinkedByImpossibility={isLinkedByImpossibility}
      />
      {(values.isUnableToRefuse || (eventTypesSelectOptions?.length && !isTypePmCodeOP)) &&
        !(isTypePmCodeOP && isErp) && (
          <PmEvents
            created={values.created}
            rescheduling={values.rescheduling}
            className={knmSharedStyles.Section}
            watchOnly={isWatchOnly}
            isLinkedByImpossibility={isLinkedByImpossibility}
          />
        )}
      <PmSubject
        className={knmSharedStyles.Section}
        watchOnly={isWatchOnly}
        inn={values.inn}
        ogrn={values.ogrn}
        snils={values.snils}
        isFiz={isFiz}
        okveds={values.okveds}
        rafpNza={values.rafpNza}
        kpp={values.kpp}
        isSonko={values.isSonko}
        inspectionResults={values.inspectionResults}
        subjectType={values.subjectTypePm}
        isNewOrganization={values.isOrganizationNew}
        organizationAutocomplete={values.organizationAutocomplete}
        checkSubjectResult={values.checkSubjectResult}
        smevRequestStatus={values.smevRequestStatus}
        isPresentInItRegistry={values.isPresentInItRegistry}
        itAccreditationResponse={values.itAccreditationResponse}
        smevItAccreditationRequestStatus={values.smevItAccreditationRequestStatus}
        subjectTypeLabel="Тип контролируемого лица"
        ervkId={values.ervkId}
        organizationsForSelect={values.organizationsForSelect}
        wasSelectedOrganizationByOtherPassport={!!values.wasSelectedOrganizationByOtherPassport}
        isLinkedByImpossibility={isLinkedByImpossibility}
        pmType={pmType}
      />

      <PmInspectors className={knmSharedStyles.Section} watchOnly={isWatchOnly} />

      {!isErp && <ErknmExperts isPm isWatchOnly={isNeedActSaveOrSign} />}

      <PmEventSubject
        className={knmSharedStyles.Section}
        watchOnly={isWatchOnly}
        knoOrganization={values.knoOrganizationPm}
        supervisionType={values.supervisionTypePm}
        objectsPm={values.objectsPm}
        reasonFieldValue={values.reasonsPm?.[0]}
        riskIndicators={riskIndicators}
        statusCode={statusCode}
        isOP={pmTypeCode === ProfKindCodesEnum.OP}
        isLinkedByImpossibility={isLinkedByImpossibility}
      />
      <PmCollaborating
        className={knmSharedStyles.Section}
        created={values.created}
        watchOnly={isWatchOnly || isNeedActSaveOrSign}
        isHasCollaboratingOrganization={values.isHasCollaboratingOrganization}
      />
      <KnmNotifications
        watchOnly={isWatchOnly}
        created={values.created}
        typeCode={pmTypeCode}
        rescheduling={values.rescheduling}
        isWatchOnlyByTestUser={isWatchOnlyByTestUser}
      />
      {pmTypeCode === ProfKindCodesEnum.PP && (
        <>
          <PmReject watchOnly={isWatchOnly} pmReject={values.pmReject} isUnableToRefuse={values.isUnableToRefuse} />
          <PmResultOfInspection
            className={knmSharedStyles.Section}
            watchOnly={isWatchOnly}
            withoutIssue={values?.withoutIssue}
            isUnableToRefuse={values.isUnableToRefuse}
            pmSubject={pmSubject}
            objectsPm={values.objectsPm}
            riskIndicators={riskIndicators}
            hasResultDecisions={!!values.pmResultDecisions?.length}
            knoOrganization={values.knoOrganizationPm}
            supervisionType={values.supervisionTypePm}
            requirementsNpa={values.requirementsNpa}
            isViolatedMandatoryRequirements={values.isViolatedMandatoryRequirements}
            values={values}
          />
          {isEnableAgreements && (
            <ErknmAgreements resultDecisionsPm={values.pmResultDecisions} agreements={values.agreements} isPm />
          )}
        </>
      )}
      {isEnablePaperlessPassportDocument && (
        <KnmDocuments
          watchOnly={true}
          formInstance={undefined}
          title="Документы ПМ"
          knmDocuments={knmData?.documents}
          disableDeleteDocuments={true}
          isPm
        />
      )}
      <KnmSignInfo
        title="Сведения о подписании"
        notificationText="Профилактическое мероприятие ранее не было подписано"
        signaturesInfo={knmData?.signatureInfo}
        knmDocuments={knmData?.documents}
        isPm
      />
      <KnmEpguNotification organizations={[organization]} isWatchOnlyByTestUser={isWatchOnlyByTestUser} type={'KNM'} />
      {isEnablePrescriptionCheck && <ErknmRepeatPassports repeatPassports={values?.repeatPassports} />}
      <KnmHistories
        title="История изменения профилактических мероприятий"
        executionTitle="История проведения профилактических мероприятий"
      />
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmPmContentStoreProps => ({
  publicPortalURL: getPublicPortalURL(state),
  statusCode: getKnmStatusCode(state),
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isErknmStatusTypeCanceled: getKnmIsErknmStatusTypeCanceled(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCantBeCarriedOut: getKnmIsErknmStatusTypeCantBeCarryOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  initialFormValues: getKnmPmInitialFormValues(state),
  isNew: getKnmIsNew(state),
  settings: getAdminFetchSettings(state),
  userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsWithDistrictSelectOptions(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  eventTypesSelectOptions: getKnmEventTypesSelectOptions(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isErp: getKnmDataVersion(state) === KnmVersionsEnum.ERP,
  isKnmAdminCanEditSubject: getKnmAdminCanEditSubject(state),
});

export const KnmPmContent = connect(mapStateToProps)(KnmPmContentWithoutStore);
